import { useEffect, useState } from 'react';
import { Trophy, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

/**
 * FinalizeNowPrompt — 2026-08-28 (owner: easier finalize). The moment a draft
 * COMPLETES the roster (draft response carries roster_filled/roster_capacity),
 * prompt the player to finalize — the #1 funnel drop-off (82 unfinalized
 * teams in prod, 6 with FULL rosters).
 *
 * 2026-10-12 Activation Fix Pack — a FIRST pick also prompts (once per user):
 * one pick is enough to go live now, the rest fill later fee-free. Guests
 * skip this variant (their first-draft moment belongs to the save prompt).
 */
const COPY = {
  full: {
    icon: Trophy,
    iconClass: 'text-yellow-500',
    title: "Team complete — but you're not playing yet!",
    desc: 'Your roster is full. Hit Finalize to lock your picks, enter the world rankings and start earning from live news. Takes 5 seconds.',
    go: 'Finalize now',
    later: 'Later',
  },
  first: {
    icon: Sparkles,
    iconClass: 'text-[hsl(var(--accent))]',
    title: 'Nice pick — you can go live right now',
    desc: "One pick is all it takes to enter the world rankings and start scoring from live news. Your other 17 slots stay open for later, and filling them never costs a roster-move fee.",
    go: 'Go live with 1 pick',
    later: 'Keep drafting',
  },
};

const FIRST_KEY = 'fws_first_pick_live_prompt_';

export default function FinalizeNowPrompt({ onGoToStage }) {
  const { user } = useAuth();
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    const onDraft = (e) => {
      const d = e?.detail || {};
      if (d.roster_move !== false || !(d.roster_capacity > 0)) return;
      if (d.roster_filled >= d.roster_capacity) { setVariant('full'); return; }
      if (d.roster_filled === 1 && user && !user.is_guest) {
        const key = FIRST_KEY + user.id;
        try {
          if (localStorage.getItem(key)) return;
          localStorage.setItem(key, '1');
        } catch (err) { /* private mode — show once this session */ }
        setVariant('first');
      }
    };
    window.addEventListener('fws:draft-success', onDraft);
    return () => window.removeEventListener('fws:draft-success', onDraft);
  }, [user]);

  const copy = COPY[variant] || COPY.full;
  const Icon = copy.icon;

  return (
    <Dialog open={!!variant} onOpenChange={(v) => { if (!v) setVariant(null); }}>
      <DialogContent className="sm:max-w-sm" data-testid="finalize-now-prompt" data-variant={variant || ''}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${copy.iconClass}`} /> {copy.title}
          </DialogTitle>
          <DialogDescription>{copy.desc}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => { setVariant(null); onGoToStage?.(); }}
            data-testid="finalize-now-go-btn"
          >
            {copy.go}
          </Button>
          <Button variant="ghost" onClick={() => setVariant(null)} data-testid="finalize-now-later-btn">
            {copy.later}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
